/**
 * The tester-facing app version (CLAUDE.md rule #91).
 *
 * `version` is package.json "version", compiled in by the whw-app-version
 * plugin in vite.config.ts. It is deliberately separate from the release
 * capability tuple (./release-capabilities.ts), which stays the operator's
 * exact-equality evidence and gains no field from this module; the build id
 * shown here is read from that same in-memory tuple, never re-derived.
 *
 * Browser-only: ./release-capabilities reads import.meta.env, so this module
 * must not be imported by plain-Node probes.
 */

import { getReleaseCapabilities } from '@/lib/release-capabilities';

// Injected by the whw-app-version plugin. Absent under plain-Node probes and
// any config that does not use that plugin, hence the typeof guard below.
declare const __APP_VERSION__: string | undefined;

const UNSET_BUILD = 'dev-unset';

export interface AppVersion {
  /** package.json "version", or '0.0.0-dev' when not compiled in. */
  version: string;
  /** First 7 characters of the build id, or 'dev' for an unpinned build. */
  shortSha: string;
  /** The capability tuple's buildId: 40-hex commit or 'dev-unset'. */
  buildId: string;
  /** "<version> (<shortSha>)" */
  label: string;
}

export function getAppVersion(): AppVersion {
  const version = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0-dev';
  const { buildId } = getReleaseCapabilities();
  const shortSha = buildId === UNSET_BUILD ? 'dev' : buildId.slice(0, 7);
  return { version, shortSha, buildId, label: `${version} (${shortSha})` };
}

function isInstalledApp(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  } catch {
    /* matchMedia unavailable */
  }
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

/**
 * Plain text a tester can paste into a bug report. Fixed English on purpose:
 * the reader is the developer, not the tester. Carries NO account id, email,
 * prayer content, or storage contents; only build and browser facts.
 */
export function getVersionDetailsText(language: string): string {
  const { version, shortSha, buildId } = getAppVersion();
  const userAgent = typeof navigator === 'undefined' ? 'unknown' : navigator.userAgent;
  return [
    `watch Him work ${version} (${shortSha})`,
    `Build: ${buildId}`,
    `Language: ${language}`,
    `Installed app: ${isInstalledApp() ? 'yes' : 'no'}`,
    `Browser: ${userAgent}`,
    `Date: ${new Date().toISOString()}`,
  ].join('\n');
}
